import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Modal, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { deacService, VacancyAbsence } from '../../../services/central-api/deac';
import MyDataTable from '../../../components/MyDataTable';
import PresenceSituationDropdown from '../../../components/deac/PresenceSituationDropdown';
import AbsenceTypeDropdown from '../../../components/deac/AbsenceTypeDropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { getPresenceStatus } from '../Vacancies/VacanciesCommons';
import PresenceAssignmentModal from './PresenceAssignmentModal';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    selectedApplications: Presence[];
    setSelectedApplications: Dispatch<SetStateAction<Presence[]>>;
    onConfirm: () => void;
    onClose: () => void;
    fixFor: 'present' | 'absent';
}

export default function PresenceCorrectionModal({ selectedApplications, setSelectedApplications, isModalVisible, setModalVisible, onConfirm, onClose, fixFor }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [step, setStep] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [presenceSituation, setPresenceSituation] = useState<boolean | null>(fixFor === 'present');
    const [absenceType, setAbsenceType] = useState<VacancyAbsence | null>(
        fixFor === 'present'
            ? null
            : selectedApplications.every(application => application.absentJustification)
                ? 'justified'
                : selectedApplications.every(application => !application.absentJustification) ? 'unjustified' : null
    );

    useEffect(() => {
        if (isModalVisible && selectedApplications.length === 0) {
            closeModal();
        }
    }, [selectedApplications]);

    async function formSubmit() {
        try {
            setIsLoading(true);
            await deacService.presenceRegistration({
                present: presenceSituation ?? false,
                absentJustification: absenceType ? absenceType : undefined,
                applications: Object.fromEntries(
                    selectedApplications.map(sa => [sa.id, sa.assignmentId ?? null])
                )
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            resetValues();
        }
    }, [isModalVisible]);

    function isFormValid(): boolean {
        const originalSituation = fixFor === 'present';
        if (fixFor === 'present') {
            if (originalSituation === presenceSituation) {
                return false;
            }
        } else {
            const originalAbsenceType = selectedApplications.every(application => application.absentJustification)
                ? 'justified'
                : selectedApplications.every(application => !application.absentJustification) ? 'unjustified' : null;

            if (originalSituation === presenceSituation && originalAbsenceType === absenceType) {
                return false;
            }
        }

        if (!presenceSituation && absenceType === null) {
            return false;
        }

        if (presenceSituation && selectedApplications.some(sa => !sa.assignmentId)) {
            return false;
        }

        return true;
    }

    function resetValues() {
        setStep(0);
        setPresenceSituation(fixFor === 'present');
        setAbsenceType(
            fixFor === 'present'
                ? null
                : selectedApplications.every(application => application.absentJustification)
                    ? 'justified'
                    : selectedApplications.every(application => !application.absentJustification) ? 'unjustified' : null
        );
    }

    const closeModal = () => {
        setModalVisible(false);
        resetValues();
        onClose();
    };

    function shouldPresentAbsenceTypeInformation(forceStatus?: 'present' | 'absent') {
        return (fixFor === 'present' && forceStatus !== 'absent') || (fixFor === 'absent' && forceStatus === 'present');
    }

    function displayTableWithAgents(header: string, forceStatus?: 'present' | 'absent') {
        return <View style={{ flex: 1, gap: 10 }}>
            {step === 2 ? <MyAppText style={styles.tableHeader}>{translate(header)}</MyAppText> : null}
            <View style={styles.cardList}>
                <MyDataTable
                    columns={[
                        {
                            name: translate('status'),
                            cell: row => getPresenceStatus(row, forceStatus),
                            grow: 0,
                        },
                        {
                            name: shouldPresentAbsenceTypeInformation(forceStatus) ? '' : translate('type'),
                            cell: (row) =>
                                shouldPresentAbsenceTypeInformation(forceStatus)
                                    ? ''
                                    : (forceStatus ? absenceType === 'justified' : row.absentJustification) ? translate('justifiedAbsence') : translate('unjustifiedAbsence'),
                            grow: 1,
                        },
                        {
                            name: translate('agentName'),
                            cell: row => row.user.warName && row.user.warName != '' ? row.user.warName : row.user.name,
                            grow: 1,
                        },
                        {
                            name: 'RF',
                            cell: row => row.user.registry,
                            grow: 1,
                        },
                        {
                            name: translate('assignment'),
                            cell: row => {
                                if (
                                    forceStatus === 'absent' ||
                                    (!forceStatus && !row.present) ||
                                    !row.assignment
                                ) {
                                    return '';
                                }
                                return row.assignment.name;
                            },
                            grow: 1,
                        },
                        {
                            name: translate('role'),
                            cell: row => row.user.role.name,
                            grow: 1,
                        },
                        {
                            name: translate('vacancyId'),
                            cell: row => row.vacancy.id,
                            grow: 0,
                            wrap: true
                        },
                        {
                            name: translate('activityTitle'),
                            cell: row => row.vacancy.title,
                            grow: 1,
                        },
                        {
                            name: translate('workplace'),
                            cell: row => row.vacancy.workplace.name,
                            grow: 1,
                            wrap: true
                        },
                        {
                            name: translate('agentActingBody'),
                            cell: row => row.user.actingBodyUnit.actingBody.name,
                            grow: 1,
                        },
                        {
                            name: translate('agentUnit'),
                            cell: row => row.user.actingBodyUnit.name,
                            grow: 1,
                        },
                        {
                            grow: 0,
                            width: '50px',
                            cell: row => <TouchableOpacity onPress={() => {
                                setSelectedApplications([...selectedApplications.filter(application => application.id != row.id)]);
                            }}>
                                <FontAwesomeIcon icon={faClose} color={theme.colors.errors['500']} fontSize={14} />
                            </TouchableOpacity>,
                            omit: step === 2
                        }
                    ]}
                    data={selectedApplications}
                />
            </View>
            <MyAppText style={styles.selectedAgents}>{translate('selectedAgents')}: {selectedApplications.length}</MyAppText>
        </View>;
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('agentPresenceRegistration')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('agentsWillHavePresenceConfirmed')}</MyAppText>
                        <MyAppText style={styles.stepHeader}>{translate('stepXofY', { x: step + 1, y: 3 })}</MyAppText>
                    </View>
                    {step === 0 || step === 2
                        ? displayTableWithAgents('beforeChange')
                        : null}

                    {step === 1
                        ? <View style={{ flexDirection: 'column', gap: 8, flex: 1 }}>
                            <PresenceSituationDropdown
                                setValue={setPresenceSituation}
                                value={presenceSituation}
                                zIndex={2}
                                labelStyle={styles.fieldsLabelStyle}
                                viewStyle={{ gap: 8 }}
                            />
                            {presenceSituation &&
                                <PresenceAssignmentModal
                                    selectedApplications={selectedApplications}
                                    setSelectedApplications={setSelectedApplications}
                                    presenceSituation={presenceSituation}
                                />
                            }
                            {presenceSituation === false
                                ? <AbsenceTypeDropdown
                                    setValue={setAbsenceType}
                                    value={absenceType}
                                    zIndex={1}
                                    labelStyle={styles.fieldsLabelStyle}
                                    viewStyle={{ gap: 8 }}
                                />
                                : null}
                        </View>
                        : null}
                    {step === 2
                        ? displayTableWithAgents('afterChange', presenceSituation ? 'present' : 'absent')
                        : null}
                    <FormActions
                        buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                        onSubmit={() => step !== 2 ? setStep(step + 1) : formSubmit()}
                        onClose={() => step !== 0 ? setStep(step - 1) : closeModal()}
                        disabled={(step === 1 && !isFormValid()) || isLoading}
                        isLoading={isLoading}
                        cancelText={step !== 2 ? 'cancel' : 'goBack'}
                        confirmText={step !== 2 ? 'forward' : 'conclude'}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '100%' : 'auto',
        maxWidth: isMobile ? '100%' : '80%',
        maxHeight: '85%',
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    stepHeader: {
        fontSize: 16,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.primary['500'],
    },
    cardList: {
        borderWidth: 1,
        borderRadius: 4,
        borderColor: theme.colors.neutral['200'],
        backgroundColor: theme.colors.dataTable.background,
        flex: 1,
    },
    selectedAgents: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
        alignSelf: 'flex-end'
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    tableHeader: {
        color: theme.colors.neutral['500'],
        fontWeight: '700',
        fontSize: 16,
        alignSelf: 'flex-start'
    },
}));
